import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, Search, X } from "lucide-react";

/**
 * Premium searchable dropdown — replaces long native <select> lists.
 * options: { value, label, sublabel?, badge?: { label, className }, group? }
 */
export default function SearchableSelect({
  value,
  onChange,
  options = [],
  label = "",
  required = false,
  disabled = false,
  loading = false,
  allowEmpty = true,
  emptyLabel = "Select…",
  searchPlaceholder = "Search…",
  className = "",
  hint = "",
  noResultsText = "No matches found",
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlight, setHighlight] = useState(0);
  const [menuStyle, setMenuStyle] = useState(null);
  const rootRef = useRef(null);
  const menuRef = useRef(null);
  const searchRef = useRef(null);

  const selected = options.find((o) => String(o.value) === String(value));

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => {
      const hay = `${o.label} ${o.sublabel || ""} ${o.badge?.label || ""} ${o.group || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [options, query]);

  const grouped = useMemo(() => {
    const hasGroups = filtered.some((o) => o.group);
    if (!hasGroups) return [{ key: "_all", label: null, items: filtered }];
    const map = new Map();
    for (const o of filtered) {
      const g = o.group || "Other";
      if (!map.has(g)) map.set(g, []);
      map.get(g).push(o);
    }
    return Array.from(map.entries()).map(([key, items]) => ({ key, label: key, items }));
  }, [filtered]);

  const flatFiltered = useMemo(() => grouped.flatMap((g) => g.items), [grouped]);

  const updateMenuPosition = useCallback(() => {
    if (!rootRef.current) return;
    const rect = rootRef.current.getBoundingClientRect();
    const viewportH = window.innerHeight;
    const gap = 6;
    const edgePad = 12;
    const searchChrome = 52;
    const preferredH = 280;
    const spaceBelow = viewportH - rect.bottom - edgePad;
    const spaceAbove = rect.top - edgePad;
    const openUp = spaceBelow < 200 && spaceAbove > spaceBelow;
    const available = Math.max(openUp ? spaceAbove - gap : spaceBelow - gap, 140);
    const listH = Math.min(preferredH, available - searchChrome);
    const menuH = listH + searchChrome;
    const menuW = Math.max(rect.width, 280);
    const leftPos = Math.min(Math.max(edgePad, rect.left), window.innerWidth - menuW - edgePad);

    setMenuStyle({
      position: "fixed",
      top: openUp ? "auto" : rect.bottom + gap,
      bottom: openUp ? viewportH - rect.top + gap : "auto",
      left: leftPos,
      right: "auto",
      width: menuW,
      maxHeight: menuH,
      zIndex: 10050,
      ["--hr-select-list-max"]: `${listH}px`,
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    updateMenuPosition();
    const onDoc = (e) => {
      const inRoot = rootRef.current?.contains(e.target);
      const inMenu = menuRef.current?.contains(e.target);
      if (!inRoot && !inMenu) setOpen(false);
    };
    const onScroll = () => updateMenuPosition();
    document.addEventListener("mousedown", onDoc);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [open, updateMenuPosition, label]);

  useEffect(() => {
    if (open) {
      setQuery("");
      setHighlight(0);
      updateMenuPosition();
      setTimeout(() => searchRef.current?.focus(), 50);
    }
  }, [open, updateMenuPosition]);

  useEffect(() => {
    setHighlight(0);
  }, [query]);

  const pick = (opt) => {
    if (!opt) {
      onChange?.("", null);
    } else {
      onChange?.(opt.value, opt);
    }
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (!open) {
      if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, flatFiltered.length - 1));
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    }
    if (e.key === "Enter" && flatFiltered[highlight]) {
      e.preventDefault();
      pick(flatFiltered[highlight]);
    }
  };

  const menuId = `hr-select-menu-${label || "dropdown"}`;

  const menuContent = open && menuStyle ? (
    <div
      ref={menuRef}
      id={menuId}
      className="hr-search-select-menu hr-search-select-menu--portal"
      role="listbox"
      style={menuStyle}
    >
      <div className="hr-search-select-search">
        <Search size={14} className="text-slate-400 shrink-0" />
        <input
          ref={searchRef}
          type="text"
          className="hr-search-select-search-input"
          placeholder={searchPlaceholder}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
        />
        {query ? (
          <button type="button" className="text-slate-400 hover:text-slate-600" onClick={() => setQuery("")}>
            <X size={14} />
          </button>
        ) : null}
      </div>

      <div className="hr-search-select-list">
        {allowEmpty && !query ? (
          <button
            type="button"
            className={`hr-search-select-option ${!value ? "hr-search-select-option--active" : ""}`}
            onClick={() => pick(null)}
          >
            <span className="text-slate-500">{emptyLabel}</span>
          </button>
        ) : null}

        {filtered.length === 0 ? (
          <p className="px-3 py-6 text-center text-sm text-slate-400">{noResultsText}</p>
        ) : (
          grouped.map((group) => (
            <div key={group.key}>
              {group.label ? (
                <div className="hr-search-select-group">{group.label}</div>
              ) : null}
              {group.items.map((opt) => {
                const idx = flatFiltered.indexOf(opt);
                const isActive = String(opt.value) === String(value);
                const isHighlight = idx === highlight;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    role="option"
                    aria-selected={isActive}
                    className={`hr-search-select-option ${
                      isActive ? "hr-search-select-option--active" : ""
                    } ${isHighlight ? "hr-search-select-option--highlight" : ""}`}
                    onMouseEnter={() => setHighlight(idx)}
                    onClick={() => pick(opt)}
                  >
                    <div className="min-w-0 flex-1 text-left">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="truncate font-medium text-slate-900">{opt.label}</span>
                        {opt.badge ? (
                          <span className={`hr-search-select-badge shrink-0 ${opt.badge.className || ""}`}>
                            {opt.badge.label}
                          </span>
                        ) : null}
                      </div>
                      {opt.sublabel ? (
                        <p className="text-[11px] text-slate-500 mt-0.5 truncate">{opt.sublabel}</p>
                      ) : null}
                    </div>
                  </button>
                );
              })}
            </div>
          ))
        )}
      </div>
    </div>
  ) : null;

  return (
    <div className={`hr-search-select ${className}`} ref={rootRef}>
      {label ? (
        <label className="hr-search-select-label">
          {label}
          {required ? " *" : ""}
        </label>
      ) : null}

      <button
        type="button"
        className={`hr-search-select-trigger ${open ? "hr-search-select-trigger--open" : ""} ${
          disabled || loading ? "hr-search-select-trigger--disabled" : ""
        }`}
        onClick={() => {
          if (disabled || loading) return;
          setOpen((v) => {
            const next = !v;
            if (next) {
              // Position in the same gesture so the menu never opens clipped
              requestAnimationFrame(() => updateMenuPosition());
            } else {
              setMenuStyle(null);
            }
            return next;
          });
        }}
        onKeyDown={onKeyDown}
        disabled={disabled || loading}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="hr-search-select-value min-w-0">
          {loading ? (
            <span className="text-slate-400">Loading…</span>
          ) : selected ? (
            <>
              <span className="truncate font-medium text-slate-900">{selected.label}</span>
              {selected.badge ? (
                <span className={`hr-search-select-badge ${selected.badge.className || ""}`}>
                  {selected.badge.label}
                </span>
              ) : null}
            </>
          ) : (
            <span className="text-slate-400">{emptyLabel}</span>
          )}
        </span>
        <ChevronDown size={16} className={`hr-search-select-chevron ${open ? "rotate-180" : ""}`} />
      </button>

      {menuContent && createPortal(menuContent, document.body)}

      {hint ? <p className="hr-search-select-hint">{hint}</p> : null}
    </div>
  );
}
